import React, { useState, useEffect, useRef } from 'react';
import { Gantt, ViewMode } from 'gantt-task-react';
import "gantt-task-react/dist/index.css";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { 
  Card, 
  Button, 
  Modal, 
  Form, 
  Input, 
  DatePicker, 
  Select, 
  InputNumber, 
  message, 
  Space, 
  Tooltip,
  Empty,
  Spin,
  Popconfirm,
  Radio
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined,
  ExpandOutlined,
  CompressOutlined,
  CalendarOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const ProjectGanttChart = ({ projectId, projectName, canEdit = false, startDate, endDate }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(ViewMode.Day);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const ganttRef = useRef(null);

  useEffect(() => {
    fetchGanttTasks();
  }, [projectId]);

  const fetchGanttTasks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${projectId}/gantt`);
      const data = await response.json();
      
      if (data.success && data.data.tasks && data.data.tasks.length > 0) {
        const formattedTasks = data.data.tasks.map(task => ({
          ...task,
          start: new Date(task.start),
          end: new Date(task.end),
          progress: task.progress || 0,
          type: task.type || 'task',
          isDisabled: !canEdit,
          styles: task.styles || {
            backgroundColor: getTaskColor(task.type),
            progressColor: getProgressColor(task.type),
            progressSelectedColor: '#1890ff'
          }
        }));
        setTasks(formattedTasks);
      } else {
        setTasks([]);
      }
    } catch (error) {
      console.error('Error fetching Gantt tasks:', error);
      message.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const getTaskColor = (type) => {
    switch (type) {
      case 'project': return '#1890ff';
      case 'milestone': return '#722ed1';
      default: return '#52c41a';
    }
  };

  const getProgressColor = (type) => {
    switch (type) {
      case 'project': return '#096dd9';
      case 'milestone': return '#531dab';
      default: return '#389e0d';
    }
  };

  const handleTaskChange = async (task) => {
    if (!canEdit) return;
    
    const updatedTasks = tasks.map(t => t.id === task.id ? task : t);
    setTasks(updatedTasks);
    
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${projectId}/gantt/task/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          start: task.start,
          end: task.end,
          progress: task.progress
        })
      });
    } catch (error) {
      console.error('Error updating task:', error);
      message.error('Failed to update task');
      fetchGanttTasks();
    }
  };

  const handleProgressChange = async (task) => {
    if (!canEdit) return;
    handleTaskChange(task);
  };

  const handleDoubleClick = (task) => {
    if (!canEdit) return;
    setEditingTask(task);
    form.setFieldsValue({
      name: task.name,
      type: task.type,
      dateRange: [dayjs(task.start), dayjs(task.end)],
      progress: task.progress,
      dependencies: task.dependencies || []
    });
    setIsModalOpen(true);
  };

  const handleAddTask = () => {
    setEditingTask(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'task',
      progress: 0,
      dateRange: startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : [dayjs(), dayjs().add(7, 'day')]
    });
    setIsModalOpen(true);
  };

  const handleSaveTask = async (values) => {
    try {
      setSaving(true);
      const taskData = {
        id: editingTask?.id || `task-${Date.now()}`,
        name: values.name,
        type: values.type,
        start: values.dateRange[0].toDate(),
        end: values.dateRange[1].toDate(),
        progress: values.progress || 0,
        dependencies: values.dependencies || [],
        styles: {
          backgroundColor: getTaskColor(values.type),
          progressColor: getProgressColor(values.type),
          progressSelectedColor: '#1890ff'
        }
      };

      if (editingTask) {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${projectId}/gantt/task/${editingTask.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(taskData)
        });
        message.success('Task updated successfully');
      } else {
        await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${projectId}/gantt/task`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ task: taskData })
        });
        message.success('Task added successfully');
      }

      setIsModalOpen(false);
      fetchGanttTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      message.error('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${projectId}/gantt/task/${taskId}`, {
        method: 'DELETE'
      });
      message.success('Task deleted successfully');
      fetchGanttTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      message.error('Failed to delete task');
    }
  };

  const handleExportPdf = async () => {
    if (!ganttRef.current || tasks.length === 0) {
      message.warning('No timeline data to export');
      return;
    }

    try {
      setExportingPdf(true);
      message.loading({ content: 'Generating PDF...', key: 'pdfExport' });

      const ganttElement = ganttRef.current;
      
      const canvas = await html2canvas(ganttElement, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        windowWidth: ganttElement.scrollWidth,
        windowHeight: ganttElement.scrollHeight
      });

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const pdfWidth = imgWidth > imgHeight ? 297 : 210;
      const pdfHeight = imgWidth > imgHeight ? 210 : 297;
      const orientation = imgWidth > imgHeight ? 'landscape' : 'portrait';

      const pdf = new jsPDF({
        orientation: orientation,
        unit: 'mm',
        format: 'a4'
      });

      const margin = 10;
      const availableWidth = pdfWidth - (margin * 2);
      const availableHeight = pdfHeight - (margin * 2) - 20;

      const ratio = Math.min(availableWidth / (imgWidth / 3.78), availableHeight / (imgHeight / 3.78));
      const scaledWidth = (imgWidth / 3.78) * ratio;
      const scaledHeight = (imgHeight / 3.78) * ratio;

      pdf.setFontSize(16);
      pdf.setTextColor(33, 33, 33);
      pdf.text(`${projectName} - Project Timeline`, margin, margin + 5);
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on: ${dayjs().format('MMMM DD, YYYY HH:mm')}`, margin, margin + 12);
      pdf.text(`View: ${viewMode}`, pdfWidth - margin - 30, margin + 12);

      const xOffset = margin + (availableWidth - scaledWidth) / 2;
      const yOffset = margin + 18;

      pdf.addImage(imgData, 'PNG', xOffset, yOffset, scaledWidth, scaledHeight);

      pdf.save(`${projectName.replace(/\s+/g, '_')}_Timeline_${dayjs().format('YYYY-MM-DD')}.pdf`);
      
      message.success({ content: 'PDF downloaded successfully!', key: 'pdfExport' });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      message.error({ content: 'Failed to generate PDF', key: 'pdfExport' });
    } finally {
      setExportingPdf(false);
    }
  };

  const TaskListHeader = ({ headerHeight, rowWidth, fontFamily, fontSize }) => {
    return (
      <div
        style={{
          height: headerHeight,
          fontFamily,
          fontSize,
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          borderBottom: '1px solid #e8e8e8',
          background: '#fafafa',
          fontWeight: 600
        }}
      >
        Task Name
      </div>
    );
  };

  const TaskListTable = ({ tasks, rowHeight, rowWidth, fontFamily, fontSize, onExpanderClick }) => {
    return (
      <div style={{ fontFamily, fontSize }}>
        {tasks.map((task, index) => (
          <div
            key={task.id}
            style={{
              height: rowHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 12px',
              borderBottom: '1px solid #f0f0f0',
              background: index % 2 === 0 ? '#fff' : '#fafafa'
            }}
          >
            <span style={{ 
              flex: 1, 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              paddingRight: 8
            }}>
              {task.type === 'milestone' && '◆ '}
              {task.type === 'project' && '📁 '}
              {task.name}
            </span>
            {canEdit && (
              <Space size="small">
                <Tooltip title="Edit">
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<EditOutlined />}
                    onClick={() => handleDoubleClick(task)}
                  />
                </Tooltip>
                <Popconfirm
                  title="Delete this task?"
                  onConfirm={() => handleDeleteTask(task.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Tooltip title="Delete">
                    <Button 
                      type="text" 
                      size="small" 
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="mt-4">
        <div className="flex justify-center items-center py-12">
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  const ganttContent = (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <CalendarOutlined className="text-lg text-blue-500" />
          <span className="font-semibold text-lg">Project Timeline</span>
        </div>
        <Space>
          <Radio.Group 
            value={viewMode} 
            onChange={(e) => setViewMode(e.target.value)}
            size="small"
          >
            <Radio.Button value={ViewMode.Day}>Day</Radio.Button>
            <Radio.Button value={ViewMode.Week}>Week</Radio.Button>
            <Radio.Button value={ViewMode.Month}>Month</Radio.Button>
          </Radio.Group>
          {canEdit && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddTask}
              size="small"
            >
              Add Task
            </Button>
          )}
          <Tooltip title="Download PDF">
            <Button 
              icon={<FilePdfOutlined />}
              onClick={handleExportPdf}
              loading={exportingPdf}
              size="small"
              disabled={tasks.length === 0}
            >
              PDF
            </Button>
          </Tooltip>
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
            <Button 
              icon={isFullscreen ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={() => setIsFullscreen(!isFullscreen)}
              size="small"
            />
          </Tooltip>
        </Space>
      </div>

      {tasks.length === 0 ? (
        <Empty 
          description={
            <span>
              No timeline tasks yet.
              {canEdit && " Click 'Add Task' to create your project timeline."}
            </span>
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <div ref={ganttRef} style={{ overflowX: 'auto', backgroundColor: '#fff' }}>
          <Gantt
            tasks={tasks}
            viewMode={viewMode}
            onDateChange={handleTaskChange}
            onProgressChange={handleProgressChange}
            onDoubleClick={handleDoubleClick}
            listCellWidth="200px"
            columnWidth={viewMode === ViewMode.Month ? 300 : viewMode === ViewMode.Week ? 150 : 60}
            barCornerRadius={4}
            barFill={60}
            handleWidth={8}
            todayColor="rgba(24, 144, 255, 0.1)"
            TaskListHeader={TaskListHeader}
            TaskListTable={TaskListTable}
          />
        </div>
      )}
    </>
  );

  return (
    <>
      {isFullscreen ? (
        <Modal
          open={isFullscreen}
          onCancel={() => setIsFullscreen(false)}
          footer={null}
          width="95vw"
          style={{ top: 20 }}
          title={`${projectName} - Timeline`}
        >
          {ganttContent}
        </Modal>
      ) : (
        <Card className="mt-4">
          {ganttContent}
        </Card>
      )}

      <Modal
        title={editingTask ? "Edit Task" : "Add New Task"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveTask}
        >
          <Form.Item
            name="name"
            label="Task Name"
            rules={[{ required: true, message: 'Please enter task name' }]}
          >
            <Input placeholder="Enter task name" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Type"
            rules={[{ required: true }]}
          >
            <Select>
              <Select.Option value="task">Task</Select.Option>
              <Select.Option value="milestone">Milestone</Select.Option>
              <Select.Option value="project">Project Phase</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Date Range"
            rules={[{ required: true, message: 'Please select date range' }]}
          >
            <RangePicker className="w-full" />
          </Form.Item>

          <Form.Item
            name="progress"
            label="Progress (%)"
          >
            <InputNumber min={0} max={100} className="w-full" />
          </Form.Item>

          <Form.Item
            name="dependencies"
            label="Dependencies"
          >
            <Select
              mode="multiple"
              placeholder="Select dependent tasks"
              options={tasks
                .filter(t => t.id !== editingTask?.id)
                .map(t => ({ label: t.name, value: t.id }))}
            />
          </Form.Item>

          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
                {editingTask ? 'Update' : 'Add'} Task
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ProjectGanttChart;
